import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { getAnnotationColor } from '../utils/annotationPresentation';
import {
  ClusterContainer,
  ClusterBubble,
  Popover,
  PopoverItem,
  ColorDot,
} from './AnnotationClusterMarker.styled';

function AnnotationClusterMarker(props) {
  const { t } = useTranslation();
  const { position, count, items, getItemColor, onItemClick, size = 20, color, onToggle } = props;
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef(null);

  const onDocClick = useCallback((e) => {
    if (!open) return;
    if (containerRef.current && !containerRef.current.contains(e.target)) {
      setOpen(false);
      setActiveIndex(-1);
    }
  }, [open]);

  useEffect(() => {
    document.addEventListener('click', onDocClick, true);
    return () => document.removeEventListener('click', onDocClick, true);
  }, [onDocClick]);

  const toggle = useCallback(() => {
    setOpen(prev => !prev);
    setActiveIndex(-1);
    if (onToggle) onToggle();
  }, [onToggle]);

  const onKeyDown = useCallback((e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (open && activeIndex >= 0) {
        if (onItemClick) onItemClick(items[activeIndex], e);
        setOpen(false);
        setActiveIndex(-1);
      } else {
        toggle();
      }
      return;
    }
    if (!open) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
      setActiveIndex(-1);
      return;
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const dir = e.key === 'ArrowDown' ? 1 : -1;
      const next = (activeIndex + dir + items.length) % items.length;
      setActiveIndex(next);
    }
  }, [open, activeIndex, items, onItemClick, toggle]);

  return (
    <ClusterContainer
      innerRef={(node) => { containerRef.current = node; }}
      style={{ left: position.x, top: position.y }}
      onClick={toggle}
      onKeyDown={onKeyDown}
      role="button"
      tabIndex={0}
      aria-label={t('annotations.cluster.ariaLabel', {
        defaultValue: 'Annotation cluster with {{count}} items',
        count
      })}
      aria-expanded={open}
    >
      <ClusterBubble size={size} bg={color}>
        {count}
      </ClusterBubble>
      {open && (
        <Popover role="menu">
          {items.map((item, idx) => (
            <PopoverItem
              key={item.id}
              selected={idx === activeIndex}
              onClick={(e) => { e.stopPropagation(); if (onItemClick) onItemClick(item, e); }}
            >
              <ColorDot color={getItemColor ? getItemColor(item) : '#1a73e8'} />
              <span style={{ fontWeight: 600 }}>{item.type}</span>
              <span style={{ color: '#5f6368', marginLeft: 'auto' }}>{new Date(item.start_time).toLocaleString()}</span>
            </PopoverItem>
          ))}
        </Popover>
      )}
    </ClusterContainer>
  );
}

AnnotationClusterMarker.propTypes = {
  position: PropTypes.shape({
    x: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    y: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
  }).isRequired,
  count: PropTypes.number.isRequired,
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  getItemColor: PropTypes.func,
  onItemClick: PropTypes.func,
  onToggle: PropTypes.func,
  size: PropTypes.number,
  color: PropTypes.string
};

AnnotationClusterMarker.defaultProps = {
  getItemColor: null,
  onItemClick: null,
  onToggle: null,
  size: 20,
  color: '#1a73e8'
};

export default AnnotationClusterMarker;
